import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DateInput from "./DateInput";
import { ModeToggle, RangeControls, RangeSummary } from "./ManualAttendanceDialog";
import AttendanceConflictsDialog from "./AttendanceConflictsDialog";
import ProjectPicker from "./ProjectPicker";
import ModuleIdChip from "./ModuleIdChip";
import { useAuth } from "../contexts/AuthContext";
import { isAttendanceOfficer } from "../lib/rbac";
import { Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

/**
 * Iter12 Phase 1 — Bulk manual attendance dialog.
 *
 * Multi-employee picker with search + "select all filtered", a shared status,
 * optional project + clock times + notes. On save, POSTs /attendance/bulk and
 * surfaces the created/skipped count. If any rows were skipped (already had a
 * daily row for that date), a yellow warning toast lists the codes.
 *
 * Iter50 — Added date-range mode (segmented toggle at the top). Range mode
 * fans out via POST /api/attendance/daily/range with skip_weekends /
 * skip_holidays toggles and a live "will create N rows" preview.
 */
export default function BulkAttendanceDialog({
    open, onOpenChange, moduleId, onSaved, defaultWorkDate,
}) {
    // Iter69 — Officer sees a locked Status (auto "Present") and a
    // locked Notes field. All other fields (Date, Employees, Project,
    // Clock-in, Clock-out) stay editable. Server also enforces this
    // — the frontend lock is UX-only.
    const { user } = useAuth();
    const officerScope = isAttendanceOfficer(user);
    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [search, setSearch] = useState("");
    const [date, setDate] = useState(() => defaultWorkDate || new Date().toISOString().slice(0, 10));
    const [status, setStatus] = useState("present");
    const [projectId, setProjectId] = useState("");
    const [clockIn, setClockIn] = useState("");
    const [clockOut, setClockOut] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    // Iter50 — range mode state
    const [rangeMode, setRangeMode] = useState(false);
    const [range, setRange] = useState(() => {
        const seed = defaultWorkDate || new Date().toISOString().slice(0, 10);
        return { date_from: seed, date_to: seed, skip_weekends: false, skip_holidays: false };
    });
    const [rangeSummary, setRangeSummary] = useState({ working_days: 0, total_rows: 0 });
    const [rangePreviewing, setRangePreviewing] = useState(false);
    // Iter52 — Conflicts dialog state
    const [conflictsState, setConflictsState] = useState({
        open: false, conflicts: [], cleanCount: 0, runSave: null,
    });

    // Reset state when the dialog opens.
    useEffect(() => {
        if (!open) return;
        const seed = defaultWorkDate || new Date().toISOString().slice(0, 10);
        setSelected(new Set());
        setSearch("");
        setDate(seed);
        setStatus("present");
        setProjectId("");
        setClockIn("");
        setClockOut("");
        setNotes("");
        setRangeMode(false);
        setRange({ date_from: seed, date_to: seed, skip_weekends: false, skip_holidays: false });
        setRangeSummary({ working_days: 0, total_rows: 0 });
    }, [open, defaultWorkDate]);

    const load = useCallback(async () => {
        if (!open || !moduleId) return;
        try {
            const [empRes, projRes] = await Promise.all([
                api.get(`/employees?module_id=${moduleId}&status=active&page_size=200`),
                api.get(`/projects?module_id=${moduleId}&active=true`),
            ]);
            const rows = empRes.data?.employees || empRes.data?.items || (Array.isArray(empRes.data) ? empRes.data : []);
            setEmployees(rows);
            setProjects(projRes.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, [open, moduleId]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        // Iter60.p43.6 — Sort by Module ID ascending, nulls last, then
        // filter. Module ID is what operators recognise from badges /
        // biometric devices, so this makes the list feel navigable.
        const _midKey = (e) => {
            const v = (e.module_employee_id || "").trim();
            return v ? [0, v.toLowerCase()] : [1, (e.full_name || "").toLowerCase()];
        };
        const sorted = [...employees].sort((a, b) => {
            const [aa, ab] = _midKey(a);
            const [ba, bb] = _midKey(b);
            if (aa !== ba) return aa - ba;
            return ab < bb ? -1 : ab > bb ? 1 : 0;
        });
        const q = search.trim().toLowerCase();
        if (!q) return sorted;
        // Iter60.p49 Item C — `employee_code` (EMP-####) dropped from
        // the client-side search predicate. Matches: name / category /
        // Module ID.
        return sorted.filter((e) =>
            (e.full_name || "").toLowerCase().includes(q) ||
            (e.worker_category || "").toLowerCase().includes(q) ||
            // Iter60.p43.6 — Real-world identifier search.
            (e.module_employee_id || "").toLowerCase().includes(q),
        );
    }, [employees, search]);

    const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.id));

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleAllFiltered = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filtered.forEach((e) => next.delete(e.id));
            } else {
                filtered.forEach((e) => next.add(e.id));
            }
            return next;
        });
    };

    // Iter50 — Preview count in range mode (dry_run).
    const rangeInvalid = rangeMode && range.date_to < range.date_from;
    useEffect(() => {
        if (!open || !rangeMode || selected.size === 0 || rangeInvalid) {
            setRangeSummary({ working_days: 0, total_rows: 0 });
            return;
        }
        let alive = true;
        setRangePreviewing(true);
        api.post("/attendance/daily/range", {
            employee_ids: Array.from(selected),
            date_from: range.date_from,
            date_to: range.date_to,
            skip_weekends: range.skip_weekends,
            skip_holidays: range.skip_holidays,
            check_in_time: clockIn || null,
            check_out_time: clockOut || null,
            status,
            project_id: projectId || null,
            notes: notes || null,
            dry_run: true,
        }).then(({ data }) => {
            if (!alive) return;
            setRangeSummary({
                working_days: data.working_days ?? 0,
                total_rows: data.would_create ?? 0,
                skipped_existing: data.skipped_existing ?? 0,
                skipped_weekends: data.skipped_weekends ?? 0,
                skipped_holidays: data.skipped_holidays ?? 0,
            });
        }).catch(() => {
            if (alive) setRangeSummary({ working_days: 0, total_rows: 0 });
        }).finally(() => { if (alive) setRangePreviewing(false); });
        return () => { alive = false; };
        // Selection changes are captured via Set-size + serialised ids.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, rangeMode, selected.size, Array.from(selected).sort().join(","), range.date_from, range.date_to, range.skip_weekends, range.skip_holidays, status, projectId, clockIn, clockOut, rangeInvalid]);

    const submit = async () => {
        if (selected.size === 0) { toast.error("Select at least one employee"); return; }
        if (rangeMode) {
            if (rangeInvalid) { toast.error("End date must be on or after start date."); return; }
        } else if (!date) { toast.error("Date is required"); return; }
        setSaving(true);
        try {
            // Iter52 — Preflight conflict check.
            const checkBody = rangeMode
                ? {
                    mode: "range",
                    employee_ids: Array.from(selected),
                    date_from: range.date_from,
                    date_to: range.date_to,
                    skip_weekends: range.skip_weekends,
                    skip_holidays: range.skip_holidays,
                }
                : {
                    mode: "bulk",
                    employee_ids: Array.from(selected),
                    date,
                };
            const check = await api.post("/attendance/conflicts/check", checkBody);
            const conflicts = check.data?.conflicts || [];
            if (conflicts.length > 0) {
                const cleanEmps = (check.data?.clean_employees || []).length;
                const datesCount = (check.data?.dates_evaluated || []).length || 1;
                // clean_employees are per-employee not per-pair; combined
                // with the number of dates we get an approximate clean count.
                const cleanCount = cleanEmps * datesCount;
                setConflictsState({
                    open: true, conflicts, cleanCount,
                    runSave: async (resolutions) => {
                        setSaving(true);
                        try { await doActualSave(resolutions); } finally { setSaving(false); }
                    },
                });
                setSaving(false);
                return;
            }
            await doActualSave([]);
        } catch (e) {
            toast.error(formatApiError(e));
            setSaving(false);
        }
    };

    const doActualSave = async (resolutions) => {
        if (rangeMode) {
            const { data } = await api.post("/attendance/daily/range", {
                employee_ids: Array.from(selected),
                date_from: range.date_from,
                date_to: range.date_to,
                skip_weekends: range.skip_weekends,
                skip_holidays: range.skip_holidays,
                check_in_time: clockIn || null,
                check_out_time: clockOut || null,
                status,
                project_id: projectId || null,
                notes: notes || null,
                resolutions,
            });
            const created = data?.created ?? 0;
            const overwrote = data?.overwrote ?? 0;
            const deleted = data?.deleted_and_recreated ?? 0;
            const skippedExisting = data?.skipped_existing ?? 0;
            const total = created + overwrote + deleted;
            if (total > 0) {
                toast.success(
                    skippedExisting > 0
                        ? `Wrote ${total} row${total === 1 ? "" : "s"}. Skipped ${skippedExisting} existing.`
                        : `Wrote ${total} row${total === 1 ? "" : "s"}.`,
                );
            } else if (skippedExisting > 0) {
                toast.warning(`No new rows. ${skippedExisting} already exist for that range.`);
            } else {
                toast.warning("No rows created — check the range and toggles.");
            }
            onOpenChange(false);
            onSaved && onSaved({ createdRange: { from: range.date_from, to: range.date_to, created: total } });
            return;
        }
        const body = {
            employee_ids: Array.from(selected),
            date, status,
            project_id: projectId || null,
            clock_in: clockIn || null,
            clock_out: clockOut || null,
            notes: notes || null,
            resolutions,
        };
        const { data } = await api.post("/attendance/bulk", body);
        const created = data?.created || 0;
        const skipped = data?.skipped || 0;
        const overwrote = data?.overwrote || 0;
        const deleted = data?.deleted_and_recreated || 0;
        const total = created + overwrote + deleted;
        if (total > 0) {
            toast.success(`Wrote attendance for ${total} employee${total === 1 ? "" : "s"}.`);
        }
        if (skipped > 0) {
            toast.info(`Skipped ${skipped} row${skipped === 1 ? "" : "s"} that already existed.`);
        }
        if (total === 0 && skipped === 0 && (data?.errors || []).length > 0) {
            toast.error(`No rows created — see console for errors.`);
            // eslint-disable-next-line no-console
            console.warn("bulk attendance errors:", data.errors);
        }
        onOpenChange(false);
        onSaved && onSaved({ ...data, createdRange: { from: date, to: date, created: total } });
    };

    return (
        <>
        <Dialog open={open && !conflictsState.open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="bulk-attendance-dialog">
                <DialogHeader>
                    <DialogTitle>Add attendance (bulk)</DialogTitle>
                </DialogHeader>
                <div className="pb-2">
                    <ModeToggle
                        value={rangeMode ? "range" : "single"}
                        onChange={(v) => setRangeMode(v === "range")}
                        testid="bulk-att-mode"
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                    {/* Left column: employee picker */}
                    <div className="border border-[color:var(--asl-border,#E5E7EB)] rounded-md" data-testid="bulk-employee-picker">
                        <div className="p-3 border-b border-[color:var(--asl-border,#E5E7EB)] space-y-2">
                            <Input
                                placeholder="Search by module ID, name, or category…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="bulk-employee-search"
                            />
                            <label className="flex items-center gap-2 text-xs">
                                <Checkbox
                                    checked={allFilteredSelected}
                                    onCheckedChange={toggleAllFiltered}
                                    data-testid="bulk-select-all"
                                />
                                <span className="asl-navy-text font-semibold">
                                    Select all filtered ({filtered.length})
                                </span>
                                <span className="ml-auto text-[color:var(--asl-muted)]">
                                    {selected.size} selected
                                </span>
                            </label>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                            {filtered.map((e) => (
                                <label
                                    key={e.id}
                                    className="flex items-center gap-2 px-3 py-1.5 text-xs border-b border-[color:var(--asl-border,#E5E7EB)] last:border-b-0 hover:bg-[#FAFAFB] cursor-pointer"
                                    data-testid={`bulk-employee-row-${e.id}`}
                                >
                                    <Checkbox
                                        checked={selected.has(e.id)}
                                        onCheckedChange={() => toggle(e.id)}
                                    />
                                    {/* Iter60.p50.1 — Primary identifier is the
                                        Module ID chip. `EMP-####` trailing text
                                        dropped per p49/p50.1 tester audit. */}
                                    <ModuleIdChip
                                        value={e.module_employee_id}
                                        testid={`bulk-employee-mid-${e.id}`}
                                    />
                                    <span className="flex-1 truncate font-medium asl-navy-text">
                                        {e.full_name}
                                    </span>
                                    <span className="text-[10px] text-[color:var(--asl-muted)] uppercase">
                                        {e.worker_category || e.employment_type || "—"}
                                    </span>
                                </label>
                            ))}
                            {filtered.length === 0 && (
                                <div className="p-4 text-center text-xs italic text-[color:var(--asl-muted)]">
                                    No employees match the search.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right column: shared fields */}
                    <div className="space-y-3">
                        {!rangeMode && (
                            <label className="block">
                                <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Date *</span>
                                <DateInput value={date} onChange={setDate}
                                    className="mt-1 w-full h-9 px-3 border rounded-md text-sm mono"
                                    style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                    data-testid="bulk-date"
                                />
                            </label>
                        )}
                        {rangeMode && (
                            <RangeControls
                                range={range}
                                onChange={setRange}
                                invalid={rangeInvalid}
                                testidPrefix="bulk-att"
                            />
                        )}
                        <label className="block">
                            <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide flex items-center gap-1.5">
                                Status *
                                {officerScope && (
                                    <TooltipProvider delayDuration={150}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className="inline-flex items-center gap-0.5 rounded-sm bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-600"
                                                    data-testid="bulk-status-locked"
                                                    aria-label="Locked — auto-set to Present for Officer submissions"
                                                >
                                                    <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-[11px]">
                                                Auto-set to Present for Officer submissions
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </span>
                            <select
                                value={status} onChange={(e) => setStatus(e.target.value)}
                                disabled={officerScope}
                                className={`mt-1 w-full h-9 px-3 border rounded-md text-sm bg-white ${officerScope ? "opacity-60 cursor-not-allowed" : ""}`}
                                style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                data-testid="bulk-status"
                            >
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                                <option value="paid_leave">Paid Leave</option>
                                <option value="unpaid_leave">Unpaid Leave</option>
                                <option value="holiday">Holiday</option>
                                <option value="weekend">Weekend</option>
                                <option value="late">Late</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Project (optional)</span>
                            <div className="mt-1">
                                {/* Iter67 F1 — Universal ProjectPicker.
                                    Empty string on onValueChange = "no
                                    project" — matches the legacy native
                                    `<option value="">` contract used by
                                    the /attendance/bulk endpoint. */}
                                <ProjectPicker
                                    projects={projects}
                                    value={projectId}
                                    onValueChange={setProjectId}
                                    allowNone
                                    testid="bulk-project"
                                />
                            </div>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Clock-in</span>
                                <input
                                    type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)}
                                    className="mt-1 w-full h-9 px-3 border rounded-md text-sm mono"
                                    style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                    data-testid="bulk-clock-in"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide">Clock-out</span>
                                <input
                                    type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)}
                                    className="mt-1 w-full h-9 px-3 border rounded-md text-sm mono"
                                    style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                    data-testid="bulk-clock-out"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-xs font-semibold asl-navy-text uppercase tracking-wide flex items-center gap-1.5">
                                Notes (optional)
                                {officerScope && (
                                    <TooltipProvider delayDuration={150}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className="inline-flex items-center gap-0.5 rounded-sm bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-600"
                                                    data-testid="bulk-notes-locked"
                                                    aria-label="Locked — use single-row Edit for row-level notes"
                                                >
                                                    <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-[11px]">
                                                Use single-row Edit for row-level notes
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </span>
                            <textarea
                                value={notes} onChange={(e) => setNotes(e.target.value)}
                                disabled={officerScope}
                                className={`mt-1 w-full min-h-[64px] px-3 py-2 border rounded-md text-sm ${officerScope ? "opacity-60 cursor-not-allowed bg-slate-50" : ""}`}
                                style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                data-testid="bulk-notes"
                                placeholder={officerScope ? "Locked for Officer submissions" : "Optional context saved on every row"}
                            />
                        </label>
                    </div>
                </div>
                {rangeMode && (
                    <div className="px-1">
                        <RangeSummary
                            summary={rangeSummary}
                            employees={selected.size}
                            invalid={rangeInvalid}
                            loading={rangePreviewing}
                            testid="bulk-att-range-summary"
                        />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                            data-testid="bulk-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={
                            saving
                            || selected.size === 0
                            || (rangeMode && (rangeInvalid || rangeSummary.total_rows === 0))
                        }
                        className="asl-crimson-bg text-white"
                        data-testid="bulk-submit"
                    >
                        {saving
                            ? "Saving…"
                            : rangeMode
                                ? `Create ${rangeSummary.total_rows || 0} row${rangeSummary.total_rows === 1 ? "" : "s"}`
                                : `Save (${selected.size})`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <AttendanceConflictsDialog
            open={conflictsState.open}
            onOpenChange={(o) => setConflictsState((s) => ({ ...s, open: o }))}
            conflicts={conflictsState.conflicts}
            cleanCount={conflictsState.cleanCount}
            onProceed={async (resolutions) => {
                setConflictsState((s) => ({ ...s, open: false }));
                if (conflictsState.runSave) await conflictsState.runSave(resolutions);
            }}
            onEditExisting={(c) => {
                setConflictsState((s) => ({ ...s, open: false }));
                onOpenChange(false);
                onSaved && onSaved({ editExisting: c });
            }}
            onGoToRecord={(c) => {
                setConflictsState((s) => ({ ...s, open: false }));
                onOpenChange(false);
                onSaved && onSaved({ goToRecord: c });
            }}
        />
        </>
    );
}
